import { Layout, Menu } from 'antd';
import classnames from 'classnames';
import React from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, matchPath, useLocation, useNavigate } from 'react-router';

import { css } from 'config/emotionCss';
import useElementHeight from 'hooks/useElementHeight';
import { forcePageReload } from 'modules/app/actions';
import useNavigationBar from 'ui/layout/NavigationBar/useNavigationBar';

import Trigger from './Trigger';

import * as styles from '../MainLayout.module.less';

const NavigationBar = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getKeys, menuItems } = useNavigationBar();
  const headerHeight = useElementHeight('#header');

  const [keys, setKeys] = React.useState(getKeys());
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  React.useEffect(() => {
    const { selectedKeys, openedKeys } = getKeys();

    setKeys({
      selectedKeys,
      ...(!isCollapsed && { openedKeys }), // Avoids opening the menu when it's collapsed
    });
  }, [location, isCollapsed]);

  const handleOpenChange = React.useCallback(
    newKeys =>
      setKeys(oldKeys => ({
        ...oldKeys,
        openedKeys: [newKeys.pop()],
      })),
    []
  );

  const handleOnClick = React.useCallback(
    ({ key, item }) => {
      if (item.props.to) {
        const { simplePath, path } = item.props.to;
        const destination = generatePath(simplePath ?? path, item.props.params);
        const isSamePage = Boolean(matchPath(location.pathname, destination));

        navigate(destination);
        setKeys(oldKeys => ({
          ...oldKeys,
          selectedKeys: [key],
        }));

        if (isSamePage) {
          // Force a app reloading when the user click in the active item
          dispatch(forcePageReload());
        }
      }
    },
    [dispatch, location, navigate]
  );

  const handleOnCollapse = React.useCallback(collapsed => setIsCollapsed(collapsed), []);

  const wrapperStyle = React.useMemo(
    () => css`
      height: calc(100vh - ${headerHeight}px);
    `,
    [headerHeight]
  );

  return (
    <Layout.Sider
      width={220}
      collapsible
      theme="light"
      id="navigation"
      breakpoint="xl"
      collapsedWidth={57}
      trigger={<Trigger />}
      onCollapse={handleOnCollapse}
      className={classnames('hidePrint frs-hide-scroll', styles.sidebar, wrapperStyle)}
    >
      <Menu
        mode="inline"
        items={menuItems}
        onClick={handleOnClick}
        openKeys={keys.openedKeys}
        onOpenChange={handleOpenChange}
        selectedKeys={keys.selectedKeys}
      />
    </Layout.Sider>
  );
};

export default NavigationBar;
